/* ============================================================
   OnlyCurve — assets/cart-drawer.js  (v3, Sept 2026)
   Full replacement. Paste over the entire existing file.

   ONE engine for the whole flow: add to cart -> drawer -> checkout.

   What changed vs the previous version (13,612 B):
   1. Add to cart = ONE request. /cart/add.js now returns the fresh
      drawer HTML in the same response (Shopify "sections" param).
      Before: 1 add + 2 full drawer renders + 3 /cart.js = 6 requests,
      and the drawer was swapped twice (visible flicker).
   2. Instant feedback. The button shows a spinner on the same frame
      as the tap, then "AJOUTÉ ✓". The drawer does NOT open on add
      (OPEN_DRAWER_ON_ADD below): the cart icon count goes up, the
      image flies to the cart icon, and the drawer content is updated
      in the background so it's ready when she opens it.
   3. Checkout button: spinner on tap, then goes straight to /checkout
      (skips the POST /cart + redirect hop). Falls back to the classic
      form submit if a quantity change hasn't been saved yet.
   4. One fetch/XHR hook instead of three stacked ones (this file,
      oc-fly-to-cart, the section's own code).
   5. No polling. The 3-second /cart.js loop and the two 1-second
      timers from mini-cart-drawer.liquid are gone; one observer,
      throttled to one run per frame, keeps the drawer text right.
   6. AddToCart still fires to Meta Pixel + Klaviyo on every add,
      and now uses /cart/add.js, which Shopify analytics records.
   7. French error message under the button when an add fails.

   Needs the matching snippets/mini-cart-drawer.liquid (Sept 2026).
   ============================================================ */

(function () {
  'use strict';

  if (window.__ocCartEngine) return;
  window.__ocCartEngine = true;

  var CURRENCY = 'MAD';
  var SECTION_ID = 'mini-cart';
  var ENGINE_HEADER = 'X-OC-Engine';

  /* false = adding from a product page does NOT open the cart drawer
     (she can add several items and open the cart when she's ready).
     true  = open the drawer after every add. */
  var OPEN_DRAWER_ON_ADD = false;

  var TXT = {
    adding: 'AJOUT EN COURS…',
    added: 'AJOUTÉ ✓',
    addingRow: 'Ajout en cours…',
    checkout: 'REDIRECTION…',
    addError: 'Ajout impossible pour le moment. Réessaie.',
    networkError: 'Connexion trop lente. Vérifie ton réseau et réessaie.'
  };

  var stale = false;
  var hiddenAt = 0;
  var openCategory = null;

  /* ============================================================
     Helpers
     ============================================================ */

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function urlOf(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (input.url) return String(input.url);
    return String(input);
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  function isCartMutationUrl(url) {
    if (typeof url !== 'string') return false;
    return (
      url.indexOf('/cart/add') !== -1 ||
      url.indexOf('/cart/change') !== -1 ||
      url.indexOf('/cart/update') !== -1 ||
      url.indexOf('/cart/clear') !== -1
    );
  }

  function isEngineRequest(init) {
    if (!init || !init.headers) return false;
    var h = init.headers;
    if (typeof h.get === 'function') return !!h.get(ENGINE_HEADER);
    return !!h[ENGINE_HEADER];
  }

  function bodyHasSections(body) {
    if (!body) return false;
    try {
      if (typeof body === 'string') {
        return body.indexOf('"sections"') !== -1 || body.indexOf('sections=') !== -1;
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) return body.has('sections');
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body.has('sections');
    } catch (e) {}
    return false;
  }

  function engineHeaders() {
    var h = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    h[ENGINE_HEADER] = '1';
    return h;
  }

  function cartPost(url, payload) {
    return window.fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: engineHeaders(),
      body: JSON.stringify(payload)
    });
  }

  function mark(name) {
    try { performance.mark(name); } catch (e) {}
  }

  function measure(name, start, end) {
    try { performance.measure(name, start, end); } catch (e) {}
  }

  /* ============================================================
     In-flight cart mutations (checkout waits for these)
     ============================================================ */

  var inflight = 0;
  var idleWaiters = [];

  function setBusyClass() {
    var drawer = getDrawer();
    if (drawer) drawer.classList.toggle('oc-drawer-busy', inflight > 0);
  }

  function beginMutation() {
    inflight++;
    setBusyClass();
  }

  function endMutation() {
    inflight = Math.max(0, inflight - 1);
    setBusyClass();
    if (inflight === 0) {
      var waiters = idleWaiters;
      idleWaiters = [];
      waiters.forEach(function (fn) { fn(); });
    }
  }

  function whenIdle(timeoutMs) {
    return new Promise(function (resolve) {
      if (inflight === 0) { resolve(); return; }
      var done = false;
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        resolve();
      }, timeoutMs);
      idleWaiters.push(function () {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve();
      });
    });
  }

  /* ============================================================
     Tracking (Meta Pixel + Klaviyo + dataLayer)
     ============================================================ */

  function makeEventId() {
    return 'atc_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  function onAdded(payload, source) {
    if (!payload) return;

    var items = payload.items && payload.items.length ? payload.items : [payload];
    var contentIds = [];
    var contents = [];
    var totalValue = 0;
    var firstTitle = '';

    items.forEach(function (item) {
      if (!item || !item.id) return;
      var qty = item.quantity || 1;
      var price = (item.final_price || item.price || 0) / 100;
      contentIds.push(String(item.id));
      contents.push({ id: String(item.id), quantity: qty, item_price: price });
      totalValue += price * qty;
      if (!firstTitle) firstTitle = item.product_title || item.title || '';
    });

    if (!contentIds.length) return;

    var eventId = makeEventId();
    var value = Number(totalValue.toFixed(2));

    if (typeof window.fbq === 'function') {
      try {
        window.fbq('track', 'AddToCart', {
          content_ids: contentIds,
          content_type: 'product',
          contents: contents,
          content_name: firstTitle,
          value: value,
          currency: CURRENCY
        }, { eventID: eventId });
      } catch (e) {
        console.warn('[oc] fbq AddToCart failed', e);
      }
    }

    var kl = window.klaviyo || window._learnq;
    if (kl && typeof kl.push === 'function') {
      try {
        kl.push(['track', 'Added to Cart', {
          $value: value,
          Currency: CURRENCY,
          ItemNames: items.map(function (i) { return i.product_title || i.title || ''; }),
          VariantIDs: contentIds,
          Quantity: items.reduce(function (n, i) { return n + (i.quantity || 1); }, 0)
        }]);
      } catch (e) {
        console.warn('[oc] Klaviyo Added to Cart failed', e);
      }
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'add_to_cart',
        event_id: eventId,
        currency: CURRENCY,
        value: value,
        items: contents
      });
    }

    document.dispatchEvent(new CustomEvent('oc:added-to-cart', {
      detail: { items: items, eventId: eventId, source: source || 'external' }
    }));
  }

  /* ============================================================
     Drawer rendering
     ============================================================ */

  function extractDrawerInner(html) {
    if (!html || typeof html !== 'string') return null;
    try {
      var node = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
      return node ? node.innerHTML : null;
    } catch (e) {
      console.warn('[oc] could not parse drawer HTML', e);
      return null;
    }
  }

  function restoreOpenCategory(drawer) {
    if (!openCategory) return;
    var head = drawer.querySelector('[data-oc-cat-toggle="' + openCategory + '"]');
    var body = drawer.querySelector('[data-oc-cat-body="' + openCategory + '"]');
    if (!head || !body) { openCategory = null; return; }
    body.removeAttribute('hidden');
    head.classList.add('open');
    var plus = head.querySelector('.oc-cat-plus');
    if (plus) plus.textContent = '−';
  }

  function renderDrawerHTML(html) {
    var drawer = getDrawer();
    if (!drawer) return false;
    var inner = extractDrawerInner(html);
    if (inner === null) return false;
    drawer.innerHTML = inner;
    drawer.__ocPending = false;
    drawer.classList.remove('oc-has-pending');
    restoreOpenCategory(drawer);
    stale = false;
    normalizeNow();
    return true;
  }

  var refreshing = null;
  var refreshAgain = false;

  function refreshDrawer() {
    if (refreshing) {
      refreshAgain = true;
      return refreshing;
    }
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    refreshing = window.fetch(cartUrl + '?section_id=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) { renderDrawerHTML(html); })
      .catch(function (e) { console.warn('[oc] drawer refresh failed', e); })
      .then(function () {
        refreshing = null;
        if (refreshAgain) {
          refreshAgain = false;
          return refreshDrawer();
        }
      });
    return refreshing;
  }

  var refreshTimer = null;

  function scheduleRefresh(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshDrawer, delay || 250);
  }

  /* ============================================================
     Normalizer — replaces the 3 intervals + 2 observers.
     Runs at most once per frame, only writes when something is off,
     so it settles after one pass.
     ============================================================ */

  function updateBadges(count) {
    document.querySelectorAll('[data-header-cart-count]').forEach(function (badge) {
      var text = count > 0 && count < 100 ? String(count) : '';
      if (count === 0) {
        if (!badge.classList.contains('hidden')) badge.classList.add('hidden');
      } else if (badge.classList.contains('hidden')) {
        badge.classList.remove('hidden');
      }
      if (badge.textContent !== text) badge.textContent = text;
    });
  }

  function replaceMAD(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var node;
    var hits = [];
    while ((node = walker.nextNode())) {
      if (node.nodeValue.indexOf('MAD') !== -1 && /\bMAD\b/.test(node.nodeValue)) hits.push(node);
    }
    hits.forEach(function (n) {
      n.nodeValue = n.nodeValue.replace(/\bMAD\b/g, 'dhs');
    });
  }

  function normalizeNow() {
    var drawer = getDrawer();
    if (!drawer) return;

    var countEl = drawer.querySelector('[data-oc-count]');
    var count = countEl ? parseInt(countEl.getAttribute('data-oc-count'), 10) || 0 : null;

    var msg = drawer.querySelector('.free-shipping-message[data-oc-msg]');
    if (msg) {
      var wanted = msg.getAttribute('data-oc-msg');
      if (msg.textContent !== wanted) msg.textContent = wanted;
    }

    var wrap = drawer.querySelector('.free-shipping-wrapper.hidden');
    if (wrap) wrap.classList.remove('hidden');

    if (count !== null && !drawer.__ocPending) {
      var label = '· ' + count + ' article' + (count !== 1 ? 's' : '');
      var drawerCount = drawer.querySelector('#cartDrawer-count');
      if (drawerCount && drawerCount.textContent !== label) drawerCount.textContent = label;
      updateBadges(count);
      document.body.classList.toggle('oc-cart-has-items', count > 0);
    }

    replaceMAD(drawer);
  }

  var normalizeFrame = 0;

  function scheduleNormalize() {
    if (normalizeFrame) return;
    normalizeFrame = requestAnimationFrame(function () {
      normalizeFrame = 0;
      normalizeNow();
    });
  }

  function observeDrawer(drawer) {
    if (!drawer || drawer.__ocObserved || typeof MutationObserver === 'undefined') return;
    drawer.__ocObserved = true;
    new MutationObserver(scheduleNormalize).observe(drawer, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /* ============================================================
     Open / close
     ============================================================ */

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('active');
    document.body.classList.add('no-scroll');
    if (stale) refreshDrawer();
    setTimeout(function () {
      if (typeof window.trapFocusElements === 'function') {
        try {
          window.trapFocusElements(drawer.querySelector('.side-drawer-modal-inner') || drawer);
        } catch (e) {}
      }
    }, 400);
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('active');
    document.body.classList.remove('no-scroll');
  }

  /* ============================================================
     Buttons: loading / success / error
     ============================================================ */

  function setBtnLoading(btn, label) {
    if (!btn || btn.getAttribute('data-oc-busy') === '1') return;
    btn.setAttribute('data-oc-busy', '1');
    btn.__ocHtml = btn.innerHTML;
    btn.__ocDisabled = btn.disabled;
    btn.classList.add('oc-is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    btn.innerHTML = '<span class="oc-spinner" aria-hidden="true"></span><span>' + label + '</span>';
  }

  function resetBtn(btn) {
    if (!btn || btn.getAttribute('data-oc-busy') !== '1') return;
    if (typeof btn.__ocHtml === 'string') btn.innerHTML = btn.__ocHtml;
    btn.disabled = !!btn.__ocDisabled;
    btn.classList.remove('oc-is-loading');
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('data-oc-busy');
  }

  function flashBtn(btn, label, ms) {
    if (!btn || !btn.isConnected) return;
    btn.innerHTML = '<span>' + label + '</span>';
    btn.classList.remove('oc-is-loading');
    setTimeout(function () { resetBtn(btn); }, ms || 1200);
  }

  function clearAtcError(form) {
    if (!form) return;
    var old = form.querySelector('.oc-atc-error');
    if (old) old.remove();
  }

  function showAtcError(form, btn, message) {
    if (!btn || !btn.isConnected) return;
    if (form) clearAtcError(form);
    var el = document.createElement('p');
    el.className = 'oc-atc-error';
    el.setAttribute('role', 'alert');
    el.textContent = message;
    btn.insertAdjacentElement('afterend', el);
    setTimeout(function () { if (el.isConnected) el.remove(); }, 6000);
  }

  /* ============================================================
     Optimistic pending row
     ============================================================ */

  function selectedVariantLabel(form) {
    var parts = [];
    form.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      if (input.value) parts.push(input.value);
    });
    form.querySelectorAll('select').forEach(function (select) {
      var opt = select.options[select.selectedIndex];
      if (opt && select.name !== 'id' && opt.text) parts.push(opt.text.trim());
    });
    return parts.join(' / ');
  }

  function buildPendingRow(form, qty) {
    var scope = form.closest('.bodysuit-info, product-form, .product-card, .card-product, .shopify-section') || form;
    var titleEl = scope.querySelector('.bodysuit-info__title, .product-title, h1, h2');
    var title = titleEl ? titleEl.textContent.trim() : '';
    if (!title) return null;

    var priceEl = scope.querySelector('[data-price], .price-sale, .price');
    var price = priceEl ? priceEl.textContent.trim() : '';

    var img = scope.querySelector('.bodysuit-info__slide.is-active img') || scope.querySelector('img');
    var src = img ? (img.currentSrc || img.src || '') : '';

    var variant = selectedVariantLabel(form);

    var row = document.createElement('div');
    row.className = 'oc-pending-row';

    var imgBox = document.createElement('div');
    imgBox.className = 'oc-pending-img';
    if (src) imgBox.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';

    var info = document.createElement('div');
    info.className = 'oc-pending-info';

    var t = document.createElement('p');
    t.className = 'oc-pending-title';
    t.textContent = title;
    info.appendChild(t);

    if (variant || price) {
      var v = document.createElement('p');
      v.className = 'oc-pending-variant';
      v.textContent = [variant, (qty > 1 ? qty + ' × ' : '') + price].filter(Boolean).join(' — ');
      info.appendChild(v);
    }

    var s = document.createElement('p');
    s.className = 'oc-pending-status';
    s.innerHTML = '<span class="oc-spinner" aria-hidden="true"></span>' + TXT.addingRow;
    info.appendChild(s);

    row.appendChild(imgBox);
    row.appendChild(info);
    return row;
  }

  function showPending(row, qty) {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.__ocPending = true;
    if (row) {
      var body = drawer.querySelector('.side-drawer-modal-body');
      if (body) {
        body.insertBefore(row, body.firstChild);
        body.scrollTop = 0;
        drawer.classList.add('oc-has-pending');
      }
    }
    var countEl = drawer.querySelector('[data-oc-count]');
    var current = countEl ? parseInt(countEl.getAttribute('data-oc-count'), 10) || 0 : 0;
    var next = current + (qty || 1);
    updateBadges(next);
    var drawerCount = drawer.querySelector('#cartDrawer-count');
    if (drawerCount) drawerCount.textContent = '· ' + next + ' article' + (next !== 1 ? 's' : '');
  }

  function clearPending() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.querySelectorAll('.oc-pending-row').forEach(function (r) { r.remove(); });
    drawer.__ocPending = false;
    drawer.classList.remove('oc-has-pending');
    normalizeNow();
  }

  /* ============================================================
     Add to cart — one request, drawer HTML in the same response
     ============================================================ */

  function postAdd(items) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 15000) : null;

    return window.fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: engineHeaders(),
      body: JSON.stringify({
        items: items,
        sections: SECTION_ID,
        sections_url: window.location.pathname
      }),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (timer) clearTimeout(timer);
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok || data.status) {
          var err = new Error(data.description || data.message || 'add failed');
          err.userMessage = data.description || '';
          throw err;
        }
        return data;
      });
    }, function (e) {
      if (timer) clearTimeout(timer);
      e.isNetwork = true;
      throw e;
    });
  }

  function runAdd(items, opts) {
    var drawer = getDrawer();
    if (!drawer) return Promise.resolve();

    var btn = opts.button;
    var form = opts.form;
    var wasOpen = drawer.classList.contains('active');
    var qty = items.reduce(function (n, i) { return n + (i.quantity || 1); }, 0);

    mark('oc:atc-tap');
    if (form) clearAtcError(form);
    setBtnLoading(btn, opts.inDrawer ? '…' : TXT.adding);

    var shouldOpen = opts.inDrawer || OPEN_DRAWER_ON_ADD;
    var row = shouldOpen && opts.optimisticRow && form ? buildPendingRow(form, qty) : null;
    showPending(row, qty);
    if (shouldOpen && !wasOpen) openDrawer();

    return postAdd(items)
      .then(function (data) {
        mark('oc:atc-response');
        measure('oc: tap → server answer', 'oc:atc-tap', 'oc:atc-response');
        onAdded(data, 'engine');
        var html = data.sections && data.sections[SECTION_ID];
        if (renderDrawerHTML(html)) return;
        return refreshDrawer();
      })
      .then(function () {
        mark('oc:atc-rendered');
        measure('oc: tap → drawer updated', 'oc:atc-tap', 'oc:atc-rendered');
        clearPending();
        if (opts.inDrawer) resetBtn(btn);
        else flashBtn(btn, TXT.added, 900);
        var quickView = document.querySelector('#product-quic-kview.active');
        if (quickView && form && quickView.contains(form)) quickView.classList.remove('active');
      })
      .catch(function (err) {
        clearPending();
        if (!wasOpen) closeDrawer();
        resetBtn(btn);
        var message = err && err.isNetwork ? TXT.networkError : (err && err.userMessage) || TXT.addError;
        if (opts.inDrawer && btn) {
          btn.textContent = 'Indisponible';
          setTimeout(function () { if (btn.isConnected) btn.textContent = 'Ajouter'; }, 2000);
        } else {
          showAtcError(form, btn, message);
        }
        console.warn('[oc] add to cart failed', err);
      });
  }

  function collectItems(form) {
    var fd = new FormData(form);
    var id = fd.get('id');
    if (!id) return null;

    var qty = parseInt(fd.get('quantity'), 10);
    if (!(qty > 0)) qty = 1;

    var item = { id: Number(id), quantity: qty };

    var props = {};
    var hasProps = false;
    fd.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && typeof value === 'string' && value !== '') {
        props[m[1]] = value;
        hasProps = true;
      }
    });
    if (hasProps) item.properties = props;

    var plan = fd.get('selling_plan');
    if (plan) item.selling_plan = Number(plan);

    return [item];
  }

  /* Capture phase: runs before the product section's own submit code
     and before product-form.js, so there is exactly one add request. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (!getDrawer()) return;

    var items = collectItems(form);
    if (!items) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var btn = e.submitter || form.querySelector('[type="submit"]');
    if (btn && btn.getAttribute('data-oc-busy') === '1') return;

    var inDrawer = !!form.closest('#minicart');
    runAdd(items, {
      form: form,
      button: btn,
      inDrawer: inDrawer,
      optimisticRow: !inDrawer
    });
  }, true);

  /* ============================================================
     Drawer interactions (delegated, so they survive every re-render)
     ============================================================ */

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    // Header cart icon: open instantly, content is already fresh.
    var toggle = target.closest('#minicart-drawer-toggle');
    if (toggle) {
      e.preventDefault();
      openDrawer();
      return;
    }

    var drawer = getDrawer();
    if (!drawer || !drawer.contains(target)) return;

    var closeEl = target.closest('[data-sideDrawer-close]');
    if (closeEl) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    var head = target.closest('[data-oc-cat-toggle]');
    if (head) {
      var handle = head.getAttribute('data-oc-cat-toggle');
      var root = head.closest('.oc-cats-card');
      if (!root) return;
      var body = root.querySelector('[data-oc-cat-body="' + handle + '"]');
      if (!body) return;
      var isOpen = !body.hasAttribute('hidden');
      root.querySelectorAll('[data-oc-cat-body]').forEach(function (b) { b.setAttribute('hidden', ''); });
      root.querySelectorAll('.oc-cat-plus').forEach(function (p) { p.textContent = '+'; });
      root.querySelectorAll('.oc-cat-head').forEach(function (h) { h.classList.remove('open'); });
      openCategory = null;
      if (!isOpen) {
        body.removeAttribute('hidden');
        head.classList.add('open');
        var plus = head.querySelector('.oc-cat-plus');
        if (plus) plus.textContent = '−';
        openCategory = handle;
      }
      return;
    }

    var addBtn = target.closest('[data-oc-qv-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.getAttribute('data-oc-busy') === '1') return;
      var card = addBtn.closest('.oc-qv-card');
      var select = card ? card.querySelector('.oc-qv-select') : null;
      if (!select || !select.value) return;
      runAdd([{ id: Number(select.value), quantity: 1 }], {
        form: null,
        button: addBtn,
        inDrawer: true,
        optimisticRow: false
      });
    }
  });

  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || !sel.classList || !sel.classList.contains('oc-qv-select')) return;
    var opt = sel.options[sel.selectedIndex];
    var card = sel.closest('.oc-qv-card');
    if (!card || !opt) return;
    var priceEl = card.querySelector('.oc-qv-price-current');
    if (priceEl && opt.getAttribute('data-price')) {
      priceEl.textContent = opt.getAttribute('data-price') + ' dhs';
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var drawer = getDrawer();
    if (drawer && drawer.classList.contains('active')) closeDrawer();
  });

  /* ---------- order note autosave ---------- */

  var noteTimer = null;
  var noteValue = null;
  var notePending = null;

  function saveNote() {
    noteTimer = null;
    if (noteValue === null) return notePending || Promise.resolve();
    var value = noteValue;
    noteValue = null;
    notePending = cartPost('/cart/update.js', { note: value })
      .catch(function (e) { console.warn('[oc] note save failed', e); })
      .then(function () { notePending = null; });
    return notePending;
  }

  function flushNote() {
    if (noteTimer) {
      clearTimeout(noteTimer);
      return saveNote();
    }
    return notePending || Promise.resolve();
  }

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('oc-notes-input')) return;
    noteValue = t.value;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(saveNote, 400);
  });

  /* ============================================================
     Checkout — instant feedback, direct navigation
     ============================================================ */

  function hasUnsavedQuantities(form) {
    if (!form) return false;
    return Array.prototype.some.call(form.querySelectorAll('input[name="updates[]"]'), function (input) {
      return String(input.value) !== String(input.getAttribute('value'));
    });
  }

  function classicCheckoutSubmit(form) {
    var flag = document.createElement('input');
    flag.type = 'hidden';
    flag.name = 'checkout';
    flag.value = '1';
    form.appendChild(flag);
    HTMLFormElement.prototype.submit.call(form);
  }

  function goToCheckout(btn) {
    setBtnLoading(btn, TXT.checkout);
    mark('oc:checkout-tap');

    Promise.all([flushNote(), whenIdle(2500)])
      .then(function () {
        var form = document.getElementById('cart');
        if (hasUnsavedQuantities(form)) {
          classicCheckoutSubmit(form);
          return;
        }
        window.location.href = '/checkout';
      })
      .catch(function () {
        var form = document.getElementById('cart');
        if (form) classicCheckoutSubmit(form);
        else window.location.href = '/checkout';
      });
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var btn = target.closest('#minicart [data-oc-checkout], #minicart button[name="checkout"]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (btn.getAttribute('data-oc-busy') === '1') return;
    goToCheckout(btn);
  }, true);

  /* Back button from checkout (iOS/Android page cache): un-freeze buttons. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-oc-busy="1"]').forEach(resetBtn);
    stale = true;
    var drawer = getDrawer();
    if (drawer && drawer.classList.contains('active')) refreshDrawer();
    else normalizeNow();
  });

  /* Tab left in the background for 30s+: cart may have changed elsewhere. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
    } else if (hiddenAt && Date.now() - hiddenAt > 30000) {
      stale = true;
    }
  });

  /* ============================================================
     The drawer element (same public methods the theme expects)
     ============================================================ */

  class MiniCart extends HTMLElement {
    connectedCallback() {
      if (this.__ocInit) return;
      this.__ocInit = true;
      observeDrawer(this);
      normalizeNow();
    }

    openDrawer(event) {
      if (event && event.preventDefault) event.preventDefault();
      openDrawer();
    }

    closeDrawer(event) {
      if (event && event.preventDefault) event.preventDefault();
      closeDrawer();
    }

    refreshCartDrawer() {
      return refreshDrawer();
    }

    updateHeaderCartCount() {
      normalizeNow();
    }

    renderContents(parsedState) {
      var html = parsedState && parsedState.sections && parsedState.sections[SECTION_ID];
      if (!renderDrawerHTML(html)) refreshDrawer();
    }

    getSectionsToRender() {
      return [{ id: SECTION_ID, section: SECTION_ID, selector: 'cart-drawer' }];
    }

    getSectionInnerHTML(html) {
      return extractDrawerInner(html);
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', MiniCart);
  }

  /* ============================================================
     One hook for cart requests made by OTHER code
     (theme quantity buttons, Crée Ton Set page, apps).
     Our own requests carry X-OC-Engine and are skipped here.
     ============================================================ */

  if (!window.__ocCartHooked) {
    window.__ocCartHooked = true;

    var previousFetch = window.fetch;
    if (typeof previousFetch === 'function') {
      window.fetch = function (input, init) {
        var url = urlOf(input);
        if (!isCartMutationUrl(url)) return previousFetch.apply(this, arguments);

        var ours = isEngineRequest(init);
        var withSections = bodyHasSections(init && init.body);

        beginMutation();
        var result = previousFetch.apply(this, arguments);

        result
          .then(function (response) {
            if (ours || !response || !response.ok) return;
            if (isCartAddUrl(url)) {
              response.clone().json().then(function (data) { onAdded(data, 'external'); }).catch(function () {});
            }
            if (withSections) scheduleNormalize();
            else scheduleRefresh(250);
          })
          .catch(function () {})
          .then(endMutation);

        return result;
      };
    }

    var OrigOpen = XMLHttpRequest.prototype.open;
    var OrigSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ocUrl = urlOf(url);
      return OrigOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      var xhr = this;
      if (isCartMutationUrl(xhr.__ocUrl)) {
        var withSections = bodyHasSections(body);
        var finished = false;
        beginMutation();
        var finish = function () {
          if (finished) return;
          finished = true;
          endMutation();
        };
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (isCartAddUrl(xhr.__ocUrl)) {
              try { onAdded(JSON.parse(xhr.responseText), 'external'); } catch (e) {}
            }
            if (withSections) scheduleNormalize();
            else scheduleRefresh(250);
          }
          finish();
        });
        xhr.addEventListener('error', finish);
        xhr.addEventListener('abort', finish);
        xhr.addEventListener('timeout', finish);
      }
      return OrigSend.apply(this, arguments);
    };
  }

  /* First pass once everything else on the page has run. */
  normalizeNow();
  window.addEventListener('load', scheduleNormalize);
})();